class OnboardingService {
  constructor() {
    this.steps = [];
    this.currentStep = 0;
    this.isActive = false;
    this.store = null;
    this.currentPage = null;
    this.pageOnboardings = {};
  }

  init(store) {
    this.store = store || { commit: () => {} };
    this.steps = [];
    this.currentStep = 0;
    this.isActive = false;
    this.updateStore();
  }

  registerPageOnboarding(pageName, stepsConfig) {
    this.pageOnboardings[pageName] = stepsConfig;
  }

  // Added 'force' parameter to bypass the timer when the help button is clicked
  startPageOnboarding(pageName, userId, force = false) {
    if (!userId) {
      console.warn('User ID required for onboarding tracking');
      return;
    }

    const key = `hasSeenOnboarding_${pageName}_${userId}`;
    const lastOnboarding = localStorage.getItem(key);
    const now = new Date();

    let shouldShow = false;
    
    if (force) {
      shouldShow = true; // Manual trigger always shows
    } else if (!lastOnboarding) {
      shouldShow = true;
    } else {
      const lastDate = new Date(lastOnboarding);
      const diffMinutes = (now - lastDate) / (1000 * 60);
      if (diffMinutes >= 43200) shouldShow = true;
    }

    if (!shouldShow) {
      console.log(`User has recently seen onboarding for ${pageName}`);
      return;
    }

    const stepsConfig = this.pageOnboardings[pageName];
    if (!stepsConfig || stepsConfig.length === 0) {
      console.warn(`No onboarding configured for page: ${pageName}`);
      return;
    }

    this.steps = [];
    this.currentPage = pageName;
    
    stepsConfig.forEach(stepConfig => {
      this.addStep(
        stepConfig.selector,
        stepConfig.message,
        {
          position: stepConfig.position || 'bottom',
          title: stepConfig.title || ''
        }
      );
    });

    if (!force) {
      localStorage.setItem(key, now.toISOString());
    }
    
    this.start();
  }

  addStep(elementSelector, message, options = {}) {
    const step = {
      elementSelector,
      message,
      position: options.position || 'bottom',
      title: options.title || '',
      completed: false
    };
    this.steps.push(step);
    this.updateStore();
  }

  start() {
    if (this.steps.length === 0) return;
    this.isActive = true;
    this.currentStep = 0;
    
    this.$nextTick(() => {
      if (!this.validateCurrentStep()) {
        this.next();
      } else {
        this.updateStore();
      }
    });
  }

  validateCurrentStep() {
    const currentStep = this.steps[this.currentStep];
    if (!currentStep) return false;
    
    const element = document.querySelector(currentStep.elementSelector);
    if (!element) {
      console.warn(`Element not found: ${currentStep.elementSelector}`);
      return false;
    }
    
    this.scrollToElement(element);
    return true;
  }

  scrollToElement(element) {
    if (!element) return;
    const scrollParent = element.closest('.main-content-wrapper') || 
                        element.closest('.dashboard-wrapper') ||
                        element.closest('.game-detail-testers');
    
    if (scrollParent) {
      const elementRect = element.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();
      
      if (elementRect.top < parentRect.top || elementRect.bottom > parentRect.bottom) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  next() {
    if (!this.isActive) return;
    if (this.steps[this.currentStep]) this.steps[this.currentStep].completed = true;
    if (this.currentStep >= this.steps.length - 1) {
      this.complete();
      return;
    }
    this.currentStep++;
    setTimeout(() => {
      if (!this.validateCurrentStep()) this.next();
      else this.updateStore();
    }, 100);
  }

  previous() {
    if (!this.isActive || this.currentStep <= 0) return;
    this.currentStep--;
    setTimeout(() => {
      if (!this.validateCurrentStep()) this.previous();
      else this.updateStore();
    }, 100);
  }

  complete() {
    this.steps.forEach(step => step.completed = true);
    this.isActive = false;
    this.currentPage = null;
    this.updateStore();
  }

  skip() {
    this.isActive = false;
    this.currentPage = null;
    this.updateStore();
  }

  updateStore() {
    if (!this.store || !this.store.commit) return;
    this.store.commit('onboarding/setOnboardingState', {
      isActive: this.isActive,
      currentStep: this.currentStep,
      steps: this.steps
    });
  }
  
  $nextTick(callback) {
    setTimeout(callback, 0);
  }
}

export const onboardingService = new OnboardingService();

// Vuex Module Export
const state = {
  isActive: false,
  currentStepIndex: 0,
  steps: []
};

const mutations = {
  setOnboardingState(state, { isActive, currentStep, steps }) {
    state.isActive = isActive;
    state.currentStepIndex = currentStep && steps && steps.length > 0 
      ? Math.min(currentStep, steps.length - 1) 
      : 0;
    state.steps = steps || [];
  }
};

export default {
  namespaced: true,
  state,
  mutations
};


// WEBPACK FOOTER //
// ./src/store/modules/onboarding.js